/**
 * Reviews Carousel — Swiper init fallback, custom pagination, accessibility.
 */
(function () {
  'use strict';

  const DESKTOP_MIN = 1025;
  const PAGINATION_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']);

  const SWIPER_DEFAULTS = {
    loop: false,
    slidesPerView: 1,
    spaceBetween: 0,
    grabCursor: false,
    centeredSlides: false,
    navigation: false,
    pagination: false,
  };

  function resolveSwiperOptions(carouselEl) {
    let options = { ...SWIPER_DEFAULTS };

    if (carouselEl.dataset.options) {
      try {
        options = { ...SWIPER_DEFAULTS, ...JSON.parse(carouselEl.dataset.options) };
      } catch (error) {
        console.warn('Reviews carousel: invalid data-options JSON', error);
      }
    }

    if (options.navigation === true) {
      options.navigation = {
        nextEl: carouselEl.querySelector('.swiper-button-next'),
        prevEl: carouselEl.querySelector('.swiper-button-prev'),
      };
    } else if (options.navigation === false) {
      delete options.navigation;
    }

    options.pagination = false;

    return options;
  }

  function getSwiperInstance(section) {
    const carousel = section.querySelector('s-carousel');
    const swiperEl = carousel?.querySelector('[js-carousel]');

    return {
      carousel,
      swiperEl,
      swiper: swiperEl?.swiper || carousel?.carousel || null,
    };
  }

  function getSwiperIndex(swiper) {
    return typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;
  }

  function parseSlideIndex(element) {
    return parseInt(element?.dataset.slideIndex, 10);
  }

  function createSwiperInstance(carousel, swiperEl) {
    const options = resolveSwiperOptions(carousel);

    try {
      return new Swiper(swiperEl, options);
    } catch (error) {
      console.error('Reviews carousel: Swiper init failed', error, options);

      return new Swiper(swiperEl, {
        ...options,
        loop: false,
        autoHeight: false,
        observer: false,
        observeParents: false,
        resizeObserver: false,
      });
    }
  }

  function initSwiperIfNeeded(section) {
    const { carousel, swiperEl, swiper } = getSwiperInstance(section);
    if (!carousel || !swiperEl) return null;

    if (swiperEl.classList.contains('swiper-initialized')) {
      return swiper;
    }

    if (typeof Swiper === 'undefined') return null;

    try {
      const instance = createSwiperInstance(carousel, swiperEl);
      carousel.carousel = instance;
      carousel.dispatchEvent(
        new CustomEvent('carousel:ready', {
          detail: { swiper: instance },
          bubbles: true,
        }),
      );
      return instance;
    } catch (error) {
      console.error('Reviews carousel: fallback init failed', error);
      return null;
    }
  }

  function setSlideVisibility(slide, visible) {
    slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  function updateSlideAccessibility(section, swiper) {
    if (!swiper?.slides) return;

    const slideCount =
      parseInt(section.dataset.slideCount, 10) || swiper.slides.length;
    const activeIndex = swiper.activeIndex;

    swiper.slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;

      const visible =
        index === activeIndex || slide.classList.contains('swiper-slide-active');

      setSlideVisibility(slide, visible);
    });

    const liveRegion = section.querySelector('[data-reviews-carousel-status]');
    if (!liveRegion) return;

    const statusIndex = getSwiperIndex(swiper) + 1;
    const template = section.dataset.slideStatusTemplate;

    liveRegion.textContent = template
      ? template
          .replace('__INDEX__', String(statusIndex))
          .replace('__COUNT__', String(slideCount))
      : `Slide ${statusIndex} of ${slideCount}`;
  }

  function setPaginationBulletState(bullets, activeIndex) {
    bullets.forEach((bullet) => {
      const bulletIndex = parseSlideIndex(bullet);
      const isActive = bulletIndex === activeIndex;

      bullet.classList.toggle('swiper-pagination-bullet-active', isActive);

      if (isActive) {
        bullet.setAttribute('aria-current', 'true');
      } else {
        bullet.removeAttribute('aria-current');
      }
    });
  }

  function goToSlide(swiper, index) {
    if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(index);
      return;
    }

    swiper.slideTo(index);
  }

  function getPaginationNextIndex(key, currentIndex, total) {
    switch (key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        return currentIndex > 0 ? currentIndex - 1 : total - 1;
      case 'ArrowRight':
      case 'ArrowDown':
        return currentIndex < total - 1 ? currentIndex + 1 : 0;
      case 'Home':
        return 0;
      case 'End':
        return total - 1;
      default:
        return currentIndex;
    }
  }

  function initPagination(section, swiper) {
    const pagination = section.querySelector('[data-reviews-carousel-pagination]');
    if (!pagination || pagination.dataset.paginationInit === 'true') return;

    const bullets = pagination.querySelectorAll('[data-slide-index]');
    if (!bullets.length) return;

    pagination.dataset.paginationInit = 'true';

    const setActiveSlide = (index) => {
      if (Number.isNaN(index) || index < 0) return;
      setPaginationBulletState(bullets, index);
      updateSlideAccessibility(section, swiper);
    };

    bullets.forEach((bullet) => {
      bullet.addEventListener('click', () => {
        const index = parseSlideIndex(bullet);
        if (Number.isNaN(index)) return;
        goToSlide(swiper, index);
      });
    });

    pagination.addEventListener('keydown', (event) => {
      if (!PAGINATION_KEYS.has(event.key)) return;

      const items = Array.from(bullets);
      const currentIndex = items.findIndex(
        (bullet) => bullet.getAttribute('aria-current') === 'true',
      );

      if (currentIndex < 0) return;

      const nextIndex = getPaginationNextIndex(
        event.key,
        currentIndex,
        items.length,
      );

      event.preventDefault();
      goToSlide(swiper, nextIndex);
      items[nextIndex].focus();
    });

    const syncFromSwiper = () => {
      setActiveSlide(getSwiperIndex(swiper));
    };

    syncFromSwiper();
    swiper.on('slideChange', syncFromSwiper);
  }

  const MOBILE_MAX = DESKTOP_MIN - 1;

  function isMobileLayout() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;
  }

  function getSlideLeftPanels(section) {
    return Array.from(
      section.querySelectorAll('.reviews-carousel__slide .reviews-carousel__panel--left'),
    ).filter((panel) => panel.closest('.swiper-slide-duplicate') === null);
  }

  function clearReviewPanelHeights(section) {
    section.style.removeProperty('--reviews-carousel-left-panel-height');

    getSlideLeftPanels(section).forEach((panel) => {
      panel.style.removeProperty('min-height');
    });
  }

  function measureLeftPanelHeight(panel) {
    const content = panel.querySelector('.reviews-carousel__panel-content--left');
    if (content instanceof HTMLElement) {
      return Math.ceil(content.scrollHeight);
    }

    return Math.ceil(panel.getBoundingClientRect().height);
  }

  function syncReviewPanelHeights(section) {
    if (!isMobileLayout()) {
      clearReviewPanelHeights(section);
      return;
    }

    const leftPanels = getSlideLeftPanels(section);
    if (!leftPanels.length) return;

    clearReviewPanelHeights(section);
    void section.offsetHeight;

    let maxHeight = 0;
    leftPanels.forEach((panel) => {
      maxHeight = Math.max(maxHeight, measureLeftPanelHeight(panel));
    });

    if (!maxHeight) return;

    section.style.setProperty(
      '--reviews-carousel-left-panel-height',
      `${maxHeight}px`,
    );
  }

  function getActiveSlideHeight(swiper, section) {
    const activeSlide = swiper.slides?.[swiper.activeIndex];
    if (!(activeSlide instanceof HTMLElement)) return 0;

    const slideInner = activeSlide.querySelector('.reviews-carousel__slide-inner');
    const target = slideInner instanceof HTMLElement ? slideInner : activeSlide;

    return Math.ceil(target.getBoundingClientRect().height);
  }

  function clearMobileSlideHeights(swiper) {
    swiper.slides?.forEach((slide) => {
      if (slide instanceof HTMLElement) {
        slide.style.removeProperty('height');
      }
    });
  }

  function syncMobileCarouselHeight(swiper, section) {
    if (swiper.destroyed || !isMobileLayout()) return;

    syncReviewPanelHeights(section);
    void section.offsetHeight;

    clearMobileSlideHeights(swiper);

    const height = getActiveSlideHeight(swiper, section);
    if (!height) return;

    swiper.el.style.height = `${height}px`;

    if (swiper.wrapperEl instanceof HTMLElement) {
      swiper.wrapperEl.style.height = `${height}px`;
    }

    swiper.slides?.forEach((slide) => {
      if (slide instanceof HTMLElement) {
        slide.style.height = `${height}px`;
      }
    });

    if (typeof swiper.updateAutoHeight === 'function') {
      swiper.updateAutoHeight(0);
    }
  }

  function updateCarouselHeight(swiper, section) {
    if (swiper.destroyed) return;

    if (isMobileLayout()) {
      syncMobileCarouselHeight(swiper, section);
      return;
    }

    clearReviewPanelHeights(section);
    swiper.el.style.removeProperty('height');

    if (swiper.wrapperEl instanceof HTMLElement) {
      swiper.wrapperEl.style.removeProperty('height');
    }

    clearMobileSlideHeights(swiper);
    swiper.update();
  }

  function observeActiveSlide(section, swiper) {
    if (section._reviewsHeightObserver) {
      section._reviewsHeightObserver.disconnect();
      section._reviewsHeightObserver = null;
    }

    if (!isMobileLayout() || swiper.destroyed) return;

    const leftPanels = getSlideLeftPanels(section);
    if (!leftPanels.length || typeof ResizeObserver === 'undefined') return;

    section._reviewsHeightObserver = new ResizeObserver(() => {
      syncMobileCarouselHeight(swiper, section);
    });

    leftPanels.forEach((panel) => {
      section._reviewsHeightObserver.observe(panel);
    });

    const activeSlide = swiper.slides?.[swiper.activeIndex];
    const slideInner = activeSlide?.querySelector('.reviews-carousel__slide-inner');

    if (slideInner instanceof HTMLElement) {
      section._reviewsHeightObserver.observe(slideInner);
    }
  }

  function bindMediaLoadSync(section, swiper) {
    if (section.dataset.reviewsMediaBound === 'true') return;

    section.dataset.reviewsMediaBound = 'true';

    const onMediaReady = () => {
      syncMobileCarouselHeight(swiper, section);
      observeActiveSlide(section, swiper);
    };

    section.querySelectorAll('img').forEach((image) => {
      if (image.complete) return;

      image.addEventListener('load', onMediaReady, { once: true });
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(onMediaReady).catch(() => {});
    }
  }

  function bindCarousel(section, swiper) {
    if (!swiper || section.dataset.a11yInit === 'true') return;

    section.dataset.a11yInit = 'true';

    const syncSlides = () => updateSlideAccessibility(section, swiper);

    syncSlides();
    initPagination(section, swiper);

    const onSlideChange = () => {
      syncSlides();
      updateCarouselHeight(swiper, section);
      observeActiveSlide(section, swiper);
    };

    swiper.on('slideChange', onSlideChange);
    swiper.on('slideChangeTransitionEnd', onSlideChange);

    const desktopMq = window.matchMedia(`(min-width: ${DESKTOP_MIN}px)`);
    const onLayoutChange = () => {
      requestAnimationFrame(() => {
        if (!swiper.destroyed) {
          updateCarouselHeight(swiper, section);
          observeActiveSlide(section, swiper);
          syncSlides();
        }
      });
    };

    bindMediaLoadSync(section, swiper);
    updateCarouselHeight(swiper, section);
    observeActiveSlide(section, swiper);
    swiper.on('breakpoint', onLayoutChange);
    swiper.on('resize', onLayoutChange);
    desktopMq.addEventListener('change', onLayoutChange);
  }

  function bindCarouselReady(section) {
    const { carousel } = getSwiperInstance(section);
    if (!carousel || carousel.dataset.reviewsBound === 'true') return;

    carousel.dataset.reviewsBound = 'true';

    carousel.addEventListener('carousel:ready', (event) => {
      bindCarousel(section, event.detail?.swiper);
    });
  }

  function ensureSwiperConfig(section) {
    const { carousel, swiperEl, swiper } = getSwiperInstance(section);
    if (!carousel || !swiperEl) return null;

    if (!swiperEl.classList.contains('swiper-initialized')) {
      return initSwiperIfNeeded(section);
    }

    if (!swiper) return initSwiperIfNeeded(section);

    const expected = resolveSwiperOptions(carousel);
    const needsReinit =
      isMobileLayout() &&
      (Boolean(swiper.params.loop) !== Boolean(expected.loop) ||
        Boolean(swiper.params.autoHeight) !== Boolean(expected.autoHeight));

    if (!needsReinit) return swiper;

    try {
      swiper.destroy(true, true);
      delete carousel.carousel;
      delete section.dataset.a11yInit;
      delete section.dataset.reviewsMediaBound;

      const pagination = section.querySelector('[data-reviews-carousel-pagination]');
      if (pagination) delete pagination.dataset.paginationInit;

      const instance = createSwiperInstance(carousel, swiperEl);
      carousel.carousel = instance;
      carousel.dispatchEvent(
        new CustomEvent('carousel:ready', {
          detail: { swiper: instance },
          bubbles: true,
        }),
      );
      return instance;
    } catch (error) {
      console.error('Reviews carousel: mobile reinit failed', error);
      return swiper;
    }
  }

  function initSection(section) {
    bindCarouselReady(section);

    const swiper = ensureSwiperConfig(section);
    if (swiper) bindCarousel(section, swiper);

    return Boolean(swiper);
  }

  function initAll(container) {
    const root = container || document;
    let ready = true;

    root.querySelectorAll('.reviews-carousel').forEach((section) => {
      if (!initSection(section)) ready = false;
    });

    return ready;
  }

  function scheduleInit(container) {
    if (initAll(container)) return;

    let attempts = 0;
    const retry = () => {
      if (initAll(container)) return;
      attempts += 1;
      if (attempts < 120) requestAnimationFrame(retry);
    };

    requestAnimationFrame(retry);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleInit());
  } else {
    scheduleInit();
  }

  window.addEventListener('load', () => {
    document.querySelectorAll('.reviews-carousel').forEach((section) => {
      const { swiper } = getSwiperInstance(section);
      if (!swiper || swiper.destroyed) return;

      updateCarouselHeight(swiper, section);
      observeActiveSlide(section, swiper);
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    scheduleInit(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.reviews-carousel');
    if (!section) return;

    if (section._reviewsHeightObserver) {
      section._reviewsHeightObserver.disconnect();
      section._reviewsHeightObserver = null;
    }

    clearReviewPanelHeights(section);
    delete section.dataset.a11yInit;
    delete section.dataset.reviewsMediaBound;

    const pagination = section.querySelector('[data-reviews-carousel-pagination]');
    if (pagination) delete pagination.dataset.paginationInit;

    const { carousel } = getSwiperInstance(section);
    if (carousel) delete carousel.dataset.reviewsBound;
  });
})();
